<script setup>
import {
  computed, onBeforeUnmount, onMounted, ref, useAttrs, useSlots,
} from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * M3 floating action button, in its three sizes: `fab`, `medium` and `large`.
 *
 * **Size follows the screen unless it is pinned.** A FAB is sized against the reach of the hand on
 * the device, not against the content, so the default grows with the breakpoint — 56dp on a phone,
 * 80dp from the small breakpoint, 96dp from medium up. Passing a size overrides that for the rare
 * case where a particular screen needs one.
 *
 * **A label makes it the extended form, and the label retracts while the reader scrolls.** The
 * label earns its space when someone arrives at a screen and is deciding what to do; once they are
 * reading, it is covering the thing they came for. It comes back when they scroll back up, or
 * reach the top.
 *
 * It draws the button and nothing else. Anchoring it to a page is HrPageSticky's job, because where
 * a floating control belongs depends on the surface under it, which this cannot see.
 *
 * @status internal
 * @group actions
 * @a11y The icon-only form, and the extended form once retracted, keep the accessible name from
 *   `label`. The retraction is visual only.
 */
defineOptions({ name: 'HrFab', inheritAttrs: false });

const props = defineProps({
  /** Material icon name. */
  icon: {
    type: String,
    default: null,
  },
  /** Label beside the icon. Setting it makes this the extended FAB. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Pins the container size instead of letting it follow the breakpoint.
   * @values fab, medium, large
   */
  size: {
    type: String,
    default: null,
    validator: (value) => ['fab', 'medium', 'large'].includes(value),
  },
  /**
   * Which of M3's FAB colour roles it carries.
   * @values primary, secondary, tertiary, surface
   */
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => ['primary', 'secondary', 'tertiary', 'surface'].includes(value),
  },
  /** Prevents interaction and drops the elevation, since nothing is raised to be pressed. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Quasar visual props forwarded to the underlying button. Ignored in the design-system theme.
   * @deprecated Will be removed when the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const slots = useSlots();
const { hasTheme } = useTheme();

const isExtended = computed(() => props.label !== null);

/*
 * Retracted while the reader is moving down the page, and restored when they turn back or reach the
 * top. Not when they merely stop: coming to rest fires no scroll event, so there is nothing to
 * restore it on without a timer, and the label staying out of the way until it is asked for is the
 * better half of that trade.
 *
 * Read from the window because a FAB is anchored to the page rather than to any one scroller; a
 * screen that scrolls somewhere else keeps its label, which is the harmless failure of the two.
 */
const isRetracted = ref(false);
let lastOffset = 0;

/*
 * Quasar moves the window itself around every dialog and bottom sheet: it pins the body and drops
 * the offset to zero on opening, then scrolls back on closing. Neither is the reader moving, so the
 * offset is not followed while the page is locked, and the scroll back to where it was is no
 * movement at all. Without this, deleting a row from a confirm dialog retracted the label.
 */
const onScroll = () => {
  if (document.qScrollPrevented === true) return;

  const offset = window.scrollY ?? 0;
  if (offset === lastOffset) return;

  isRetracted.value = offset > lastOffset && offset > 0;
  lastOffset = offset;
};

onMounted(() => {
  if (!isExtended.value) return;
  lastOffset = window.scrollY ?? 0;
  window.addEventListener('scroll', onScroll, { passive: true });
});

onBeforeUnmount(() => window.removeEventListener('scroll', onScroll));

/*
 * Themed only. Outside the theme the control is Quasar's FAB rather than an unstyled one: MD2 has a
 * floating action button of its own and it is the right one there, so none of M3's shape applies.
 */
const classes = computed(() => [
  'hr-fab',
  props.size === null ? 'hr-fab--size-adaptive' : `hr-fab--size-${props.size}`,
  isExtended.value ? 'hr-fab--extended' : 'hr-fab--icon-only',
  isExtended.value && isRetracted.value && 'hr-fab--retracted',
]);

const effectiveProps = computed(() => {
  const base = {
    noCaps: true,
    ...(props.icon !== null && { icon: props.icon }),
    /*
     * The name is stated as well as rendered: the retracted label is still in the DOM but reduced to
     * no width and no opacity, which is exactly the shape assistive technology is entitled to skip.
     */
    ...(isExtended.value && { label: props.label, 'aria-label': props.label }),
    disable: props.disable,
  };

  if (hasTheme.value) {
    return {
      ...base,
      unelevated: true,
      ...(!isExtended.value && { round: true }),
      ...attrs,
    };
  }

  /*
   * Quasar's own FAB outside the theme, rather than M3's shape without M3's palette. MD2 already
   * has a floating action button — a fully rounded container with its own elevation — and it is
   * the one every other legacy screen shows. Reshaping it to M3 gives the legacy theme a control
   * that belongs to neither.
   */
  return { ...base, fab: true, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-btn
    v-bind="effectiveProps"
    :class="hasTheme ? [classes, `hr-fab--${variant}`] : undefined"
  >
    <!-- @slot Overlay content placed inside the button — a menu, a tooltip, a badge. -->
    <slot v-if="slots.default" />
  </q-btn>
</template>

<style lang="scss" scoped>
/*
 * Elevation is what makes a FAB read as floating over the content rather than sitting in it, so it
 * is on the resting state rather than reserved for hover. A disabled one drops it: nothing is
 * raised to be pressed.
 */
.hr-fab {
  overflow: hidden;
  box-shadow: var(--app-elevation-3);
  transition: min-width var(--app-comp-fab-collapse-duration) ease;
}

/*
 * The label is one line whatever width the container is passing through. Mid-retraction the button
 * is narrower than its own text, and without this the row breaks — the icon onto one line and the
 * label onto the next, or the label across two of its own.
 */
.hr-fab :deep(.q-btn__content) {
  flex-wrap: nowrap;
  white-space: nowrap;
}

.hr-fab.disabled {
  box-shadow: none;
}

// ── The three sizes ──────────────────────────────────────────────────
@mixin fab-size($name) {
  min-height: var(--app-comp-fab-container-size-#{$name});
  border-radius: var(--app-comp-fab-shape-#{$name});

  &.hr-fab--icon-only,
  &.hr-fab--retracted {
    min-width: var(--app-comp-fab-container-size-#{$name});
    width: var(--app-comp-fab-container-size-#{$name});
    padding-inline: 0;
  }

  &.hr-fab--extended {
    padding-inline: var(--app-comp-fab-extended-padding-#{$name});
  }

  :deep(.q-icon) {
    font-size: var(--app-comp-fab-icon-size-#{$name});
  }
}

.hr-fab--size-fab { @include fab-size(fab); }
.hr-fab--size-medium { @include fab-size(medium); }
.hr-fab--size-large { @include fab-size(large); }

/*
 * A FAB is sized against the reach of the hand on the device rather than against its content, so
 * the default grows with the breakpoint. Quasar's own boundaries, so a screen mixing this with
 * $q.screen sees one answer.
 */
.hr-fab--size-adaptive { @include fab-size(fab); }

@media (min-width: 600px) {
  .hr-fab--size-adaptive { @include fab-size(medium); }
}

@media (min-width: 1024px) {
  .hr-fab--size-adaptive { @include fab-size(large); }
}

// ── The extended form, and its retraction ────────────────────────────
.hr-fab--extended {
  font-size: var(--app-comp-fab-label-text-size);
}

/*
 * The label is animated out rather than removed, so the control keeps its accessible name and the
 * width change has something to interpolate. Matched as "the thing beside the icon" rather than by
 * Quasar's class for it, which is an implementation detail of how the label happens to be wrapped.
 */
.hr-fab--extended :deep(.q-btn__content > :not(.q-icon)) {
  max-width: 16rem;
  overflow: hidden;
  transition:
    max-width var(--app-comp-fab-collapse-duration) ease,
    opacity var(--app-comp-fab-collapse-duration) ease;
}

.hr-fab--extended :deep(.q-icon) {
  margin-inline-end: var(--app-comp-fab-extended-gap);
  transition: margin-inline-end var(--app-comp-fab-collapse-duration) ease;
}

.hr-fab--retracted :deep(.q-btn__content > :not(.q-icon)) {
  max-width: 0;
  opacity: 0;
}

.hr-fab--retracted :deep(.q-icon) {
  margin-inline-end: 0;
}

// ── Colour roles ─────────────────────────────────────────────────────
.hr-fab--primary {
  background: var(--app-primary-container);
  color: var(--app-on-primary-container);
}

.hr-fab--secondary {
  background: var(--app-secondary-container);
  color: var(--app-on-secondary-container);
}

.hr-fab--tertiary {
  background: var(--app-tertiary-container);
  color: var(--app-on-tertiary-container);
}

.hr-fab--surface {
  background: var(--app-surface-container-high);
  color: var(--app-primary);
}
</style>
